/**
 * The React seam between the V2 screens and the server.
 *
 * This replaces `mock/nutritionV2Store.ts`. That store was synchronous — `useSyncExternalStore`
 * over a localStorage blob — so every screen could treat "the menus" as a value that was simply
 * there. The API is not synchronous, and pretending otherwise is what produces the classic bug in
 * this shape: an empty list rendering as "אין תפריטים עדיין" for the half-second before the
 * request lands, so a trainer with twelve menus is told they have none.
 *
 * Hence every hook here reports `loading` and `error` alongside its data, and the screens are
 * expected to say which of the three they are in. The mutations are NOT hooks — they are plain
 * calls on `NutritionV2Service`, followed by the caller's `reload()`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../../../../services/NutritionV2Service";
import type {
  CompanyFoodV2,
  MenuTemplateV2,
  MenuV2,
  SavedMealV2,
} from "../model/nutritionV2Model";

export type AsyncList<T> = {
  data: T[];
  loading: boolean;
  error: unknown;
  reload: () => void;
};

/**
 * Load a list, and re-load on demand.
 *
 * `loading` starts true so the first paint is a loading state rather than an empty one. A response
 * from a superseded request is dropped: the trainee id can change while a request is in flight
 * (the trainer clicks through two clients), and the slower response would otherwise overwrite the
 * newer one with the previous trainee's menus.
 */
const useAsyncList = <T,>(load: () => Promise<T[]>, deps: unknown[]): AsyncList<T> => {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [nonce, setNonce] = useState(0);
  const requestId = useRef(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(load, deps);

  useEffect(() => {
    const id = ++requestId.current;
    let cancelled = false;
    setLoading(true);
    setError(null);

    run()
      .then((result) => {
        if (cancelled || id !== requestId.current) return;
        setData(result);
      })
      .catch((e) => {
        if (cancelled || id !== requestId.current) return;
        setError(e);
        // Deliberately NOT cleared to []: a failed refresh should leave what was on screen alone
        // rather than replace a real list with an empty one that reads as "you have none".
      })
      .finally(() => {
        if (cancelled || id !== requestId.current) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [run, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload };
};

/** Every menu a trainee has, newest first — including any they wrote for themselves. */
export const useMenusV2 = (traineeId: string): AsyncList<MenuV2> =>
  useAsyncList<MenuV2>(() => api.listMenus(traineeId), [traineeId]);

export const useSavedMealsV2 = (): AsyncList<SavedMealV2> =>
  useAsyncList<SavedMealV2>(() => api.listSavedMeals(), []);

export const useMenuTemplatesV2 = (): AsyncList<MenuTemplateV2> =>
  useAsyncList<MenuTemplateV2>(() => api.listMenuTemplates(), []);

export const useCompanyFoodsV2 = (): AsyncList<CompanyFoodV2> =>
  useAsyncList<CompanyFoodV2>(() => api.listCompanyFoods(), []);

/**
 * One record, for a builder.
 *
 * Returns `undefined` while loading AND when there is no id, so the caller has to distinguish them
 * by `loading` rather than by the absence of a value — the difference between "still fetching" and
 * "this is a blank one" is the difference between a spinner and an empty builder.
 */
const useAsyncOne = <T,>(
  id: string | undefined,
  load: (id: string) => Promise<T>
): { data: T | undefined; loading: boolean; error: unknown } => {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState<unknown>(null);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!id) {
      setData(undefined);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    loadRef
      .current(id)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  return { data, loading, error };
};

/** One menu, for the builder. */
export const useMenuV2 = (menuId: string | undefined): {
  menu: MenuV2 | undefined;
  loading: boolean;
  error: unknown;
} => {
  const { data, loading, error } = useAsyncOne<MenuV2>(menuId, api.getMenu);
  return { menu: data, loading, error };
};

/** One company menu template, for the same builder in template mode. */
export const useMenuTemplateV2 = (templateId: string | undefined): {
  template: MenuTemplateV2 | undefined;
  loading: boolean;
  error: unknown;
} => {
  const { data, loading, error } = useAsyncOne<MenuTemplateV2>(templateId, api.getMenuTemplate);
  return { template: data, loading, error };
};
